// Canonical short-slug <-> full Directus category-name map.
// Keep in sync with the products `category` field in Directus.
// Imported by:
//   - components/ui/shop-by-need-tiles.tsx (renders the tile labels)
//   - app/products/page-content.tsx (URL param resolution + per-category CTA)
import { Bug, Droplets, Shield, Sprout, Wrench, Zap } from "lucide-react"
import type { LucideIcon } from "lucide-react"

export interface CategoryEntry {
  /** URL slug (lowercase, hyphenated): "fertilizer" */
  slug: string
  /** Short tile label: "Feed" */
  label: string
  /** Full Directus category name */
  category: string
  icon: LucideIcon
}

export const PRODUCT_CATEGORIES: CategoryEntry[] = [
  { slug: "fertilizer", label: "Feed", category: "Fertilizers & Soil Health", icon: Zap },
  { slug: "pests", label: "Pests", category: "Pest & Disease Control", icon: Bug },
  { slug: "seed", label: "Seed", category: "Grass Seed & Lawn Repair", icon: Sprout },
  { slug: "tools", label: "Tools", category: "Lawn Mowers & Maintenance Tools", icon: Wrench },
  { slug: "water", label: "Water", category: "Watering & Irrigation", icon: Droplets },
  { slug: "weeds", label: "Weeds", category: "Weed Control", icon: Shield },
]

/**
 * Resolve a `?category=...` URL param to a full Directus category name.
 * Tries (in order): canonical slug, full category name, short tile label.
 * Returns null when nothing matches so the caller can fall through gracefully.
 */
export function resolveCategoryFromUrl(param: string | null | undefined): string | null {
  if (!param) return null
  const normalized = param.toLowerCase().trim()
  const bySlug = PRODUCT_CATEGORIES.find((c) => c.slug === normalized)
  if (bySlug) return bySlug.category
  const byName = PRODUCT_CATEGORIES.find((c) => c.category.toLowerCase() === normalized)
  if (byName) return byName.category
  const byLabel = PRODUCT_CATEGORIES.find((c) => c.label.toLowerCase() === normalized)
  if (byLabel) return byLabel.category
  return null
}
